import React, { useCallback, useMemo, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, ColDef, GridReadyEvent } from 'ag-grid-community';
import Spinner from './Spinner';

export interface AdminListUser {
  id: number;
  username: string;
  email: string;
  created_at: string;
  full_name?: string;
  team_name?: string;
  login_count?: number;
  last_login?: string;
}

export type UserListPeriod = 'all' | 'week' | 'month' | 'custom';

interface AdminUserListTabProps {
  users: AdminListUser[];
  loading: boolean;
  emptyMessage: string;
  onEditUser?: (user: AdminListUser) => void;
  exportFileName: string;
  metricLabel?: string;
  metricCount?: number | null;
}

const CSV_HEADERS = ['Username', 'Email', 'Full Name', 'Team Name', 'Login Counts', 'Last Login', 'Registration Date'];

const PERIOD_OPTIONS: { value: UserListPeriod; label: string }[] = [
  { value: 'all', label: 'All Time' },
  { value: 'month', label: 'Last Month' },
  { value: 'week', label: 'Last Week' },
  { value: 'custom', label: 'Custom Range' },
];

const formatDate = (value?: string) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString();
};

const escapeCsvValue = (value: string | number | undefined | null) => {
  const str = value == null ? '' : String(value);
  if (/[",\n]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

const exportUsersToCsv = (users: AdminListUser[], fileName: string) => {
  const rows = users.map((user) => [
    escapeCsvValue(user.username),
    escapeCsvValue(user.email),
    escapeCsvValue(user.full_name || ''),
    escapeCsvValue(user.team_name || ''),
    escapeCsvValue(user.login_count ?? 0),
    escapeCsvValue(formatDate(user.last_login)),
    escapeCsvValue(formatDate(user.created_at)),
  ]);

  const csv = [CSV_HEADERS.join(','), ...rows.map((row) => row.join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
};

const getPeriodRange = (
  period: UserListPeriod,
  customStart: string,
  customEnd: string
): { start: Date | null; end: Date | null } => {
  const now = new Date();

  if (period === 'week') {
    const start = new Date(now);
    start.setDate(start.getDate() - 7);
    start.setHours(0, 0, 0, 0);
    return { start, end: now };
  }

  if (period === 'month') {
    const start = new Date(now);
    start.setMonth(start.getMonth() - 1);
    start.setHours(0, 0, 0, 0);
    return { start, end: now };
  }

  if (period === 'custom' && customStart && customEnd) {
    const start = new Date(customStart);
    const end = new Date(customEnd);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  }

  return { start: null, end: null };
};

const matchesDateFilter = (
  dateValue: string | undefined,
  period: UserListPeriod,
  customStart: string,
  customEnd: string
) => {
  if (period === 'all') return true;

  const { start, end } = getPeriodRange(period, customStart, customEnd);
  if (!start || !end) return true;

  if (!dateValue) return false;

  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return false;

  return date >= start && date <= end;
};

interface DateFilterProps {
  label: string;
  period: UserListPeriod;
  customStart: string;
  customEnd: string;
  onPeriodChange: (period: UserListPeriod) => void;
  onCustomStartChange: (value: string) => void;
  onCustomEndChange: (value: string) => void;
}

const DateFilter: React.FC<DateFilterProps> = ({
  label,
  period,
  customStart,
  customEnd,
  onPeriodChange,
  onCustomStartChange,
  onCustomEndChange,
}) => (
  <div className="flex flex-wrap items-end gap-2">
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      <select
        className="bg-white border border-gray-300 text-gray-700 text-sm rounded-lg px-3 py-2 min-w-[130px]"
        value={period}
        onChange={(e) => onPeriodChange(e.target.value as UserListPeriod)}
      >
        {PERIOD_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    </div>
    {period === 'custom' && (
      <div className="flex items-center gap-2">
        <input
          type="date"
          value={customStart}
          onChange={(e) => onCustomStartChange(e.target.value)}
          className="bg-white border border-gray-300 text-gray-700 text-sm rounded-lg px-3 py-2"
        />
        <span className="text-gray-400 text-sm pb-2">to</span>
        <input
          type="date"
          value={customEnd}
          onChange={(e) => onCustomEndChange(e.target.value)}
          className="bg-white border border-gray-300 text-gray-700 text-sm rounded-lg px-3 py-2"
        />
      </div>
    )}
  </div>
);

const AdminUserListTab: React.FC<AdminUserListTabProps> = ({
  users,
  loading,
  emptyMessage,
  onEditUser,
  exportFileName,
  metricLabel,
  metricCount,
}) => {
  const gridRef = useRef<AgGridReact>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [registrationPeriod, setRegistrationPeriod] = useState<UserListPeriod>('all');
  const [loginPeriod, setLoginPeriod] = useState<UserListPeriod>('all');
  const [regCustomStart, setRegCustomStart] = useState('');
  const [regCustomEnd, setRegCustomEnd] = useState('');
  const [loginCustomStart, setLoginCustomStart] = useState('');
  const [loginCustomEnd, setLoginCustomEnd] = useState('');
  const [pageSize, setPageSize] = useState(20);
  const [selectedCount, setSelectedCount] = useState(0);

  const filteredUsers = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return users.filter((user) => {
      if (query) {
        const haystack = [user.username, user.email, user.full_name, user.team_name]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(query)) return false;
      }

      if (!matchesDateFilter(user.created_at, registrationPeriod, regCustomStart, regCustomEnd)) {
        return false;
      }

      if (!matchesDateFilter(user.last_login, loginPeriod, loginCustomStart, loginCustomEnd)) {
        return false;
      }

      return true;
    });
  }, [
    users,
    searchQuery,
    registrationPeriod,
    loginPeriod,
    regCustomStart,
    regCustomEnd,
    loginCustomStart,
    loginCustomEnd,
  ]);

  const dateFormatter = useCallback((params: { value?: string }) => formatDate(params.value), []);

  const editUserRenderer = useCallback(
    (params: { data: AdminListUser }) => {
      if (!onEditUser) return null;
      return (
        <button
          onClick={() => onEditUser(params.data)}
          className="px-3 py-1 text-xs rounded bg-blue-600 text-white hover:bg-blue-700"
        >
          Edit
        </button>
      );
    },
    [onEditUser]
  );

  const columnDefs = useMemo<ColDef[]>(() => {
    const cols: ColDef[] = [
      { field: 'username', headerName: 'Username', filter: true, sortable: true },
      { field: 'email', headerName: 'Email', filter: true, sortable: true },
      { field: 'full_name', headerName: 'Full Name', filter: true, sortable: true },
      { field: 'team_name', headerName: 'Team Name', filter: true, sortable: true },
      {
        field: 'login_count',
        headerName: 'Login Counts',
        filter: true,
        sortable: true,
        width: 130,
        valueGetter: (params) => params.data?.login_count ?? 0,
      },
      {
        field: 'last_login',
        headerName: 'Last Login',
        filter: true,
        sortable: true,
        cellRenderer: dateFormatter,
        width: 130,
      },
      {
        field: 'created_at',
        headerName: 'Registration Date',
        filter: true,
        sortable: true,
        cellRenderer: dateFormatter,
        width: 150,
      },
    ];

    if (onEditUser) {
      cols.push({
        field: 'edit',
        headerName: 'Edit',
        cellRenderer: editUserRenderer,
        sortable: false,
        filter: false,
        width: 100,
      });
    }

    return cols;
  }, [dateFormatter, editUserRenderer, onEditUser]);

  const defaultColDef = useMemo(() => ({ flex: 1, minWidth: 100, resizable: true }), []);
  const onGridReady = useCallback((params: GridReadyEvent) => {
    params.api.sizeColumnsToFit();
  }, []);

  const updateSelectionCount = useCallback(() => {
    const selected = gridRef.current?.api?.getSelectedRows() ?? [];
    setSelectedCount(selected.length);
  }, []);

  const handleExportCsv = () => {
    const selectedRows = (gridRef.current?.api?.getSelectedRows() ?? []) as AdminListUser[];
    const rowsToExport = selectedRows.length > 0 ? selectedRows : filteredUsers;
    if (rowsToExport.length === 0) return;
    exportUsersToCsv(rowsToExport, exportFileName);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="p-4 bg-white shadow-md rounded-md mb-6">
      {metricLabel != null && metricCount != null && (
        <div className="mb-4 max-w-xs">
          <div className="bg-white shadow-md rounded-md p-4 border border-gray-100">
            <p className="text-xs font-bold text-gray-500 uppercase mb-1">{metricLabel}</p>
            <p className="text-2xl font-semibold text-gray-800">{metricCount}</p>
          </div>
        </div>
      )}

      <div className="space-y-3 mb-4">
        <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-500 mb-1">Search</label>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Username, email, name, or team..."
              className="w-full bg-white border border-gray-300 text-gray-700 text-sm rounded-lg px-3 py-2"
            />
          </div>
          <button
            type="button"
            onClick={handleExportCsv}
            disabled={filteredUsers.length === 0}
            className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
          >
            Export CSV
          </button>
        </div>

        <div className="flex flex-col lg:flex-row lg:items-end gap-4 pt-1 border-t border-gray-100">
          <DateFilter
            label="Registration Date"
            period={registrationPeriod}
            customStart={regCustomStart}
            customEnd={regCustomEnd}
            onPeriodChange={setRegistrationPeriod}
            onCustomStartChange={setRegCustomStart}
            onCustomEndChange={setRegCustomEnd}
          />
          <DateFilter
            label="Last Login"
            period={loginPeriod}
            customStart={loginCustomStart}
            customEnd={loginCustomEnd}
            onPeriodChange={setLoginPeriod}
            onCustomStartChange={setLoginCustomStart}
            onCustomEndChange={setLoginCustomEnd}
          />
        </div>
      </div>

      <p className="text-xs text-gray-400 mb-3">
        Uncheck rows to exclude from export.{selectedCount > 0 ? ` ${selectedCount} selected.` : ''}
      </p>

      <div className="ag-theme-alpine w-full h-[500px] overflow-x-auto">
        <AgGridReact
          ref={gridRef}
          rowData={filteredUsers}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          modules={[AllCommunityModule]}
          animateRows
          onGridReady={onGridReady}
          pagination
          paginationPageSize={pageSize}
          paginationPageSizeSelector={[10, 20, 50, 100]}
          onPaginationChanged={(event) => setPageSize(event.api.paginationGetPageSize())}
          domLayout="normal"
          enableCellTextSelection={true}
          ensureDomOrder={true}
          rowSelection={{
            mode: 'multiRow',
            checkboxes: true,
            headerCheckbox: true,
            enableClickSelection: false,
          }}
          onSelectionChanged={updateSelectionCount}
          getRowId={(params) => String(params.data.id)}
        />
      </div>

      {filteredUsers.length === 0 && (
        <div className="text-center p-8 bg-gray-50 rounded-lg mt-4">
          <p className="text-gray-500">{emptyMessage}</p>
        </div>
      )}
    </div>
  );
};

export default AdminUserListTab;
